import type { UserProfile } from '@/hooks/useUserProfile';
import type {
  IAuthService,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SendOTPRequest,
  VerifyOTPRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  DeleteAccountResponse,
  UpdateProfileResponse,
  User,
  Auth0User,
} from '@/services/interfaces/Auth';
import axios, { type AxiosInstance, AxiosError } from 'axios';


// 定义AuthError类
export class AuthError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

export class AuthService implements IAuthService {
  private api: AxiosInstance;
  private tokenKey = 'translator_auth_token';

  constructor(baseUrl: string) {
    this.api = axios.create({
      baseURL: baseUrl + '/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器：添加认证token
    this.api.interceptors.request.use(config => {
      const token = this.getStoredToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // 响应拦截器：处理错误
    this.api.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        if (error.response?.data) {
          const data = error.response.data as any;
          throw new AuthError(
            data.code || 'unknown_error',
            data.message || data.error || '请求失败',
            data
          );
        }
        throw new AuthError(
          'network_error',
          '网络请求失败，请检查网络连接',
          error
        );
      }
    );
  }

  private async request<T>(endpoint: string, options: any = {}): Promise<T> {
    try {
      const response = await this.api.request<T>({
        url: endpoint,
        ...options,
      });
      return response.data;
    } catch (error) {
      throw error; // 错误已经在拦截器中处理
    }
  }

  private getStoredToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private setStoredToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  private removeStoredToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      data: request,
    });

    if (response.token) {
      this.setStoredToken(response.token);
    }

    return response;
  }

  async register(request: RegisterRequest): Promise<RegisterResponse> {
    const response = await this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      data: request,
    });

    if (response.token) {
      this.setStoredToken(response.token);
    }

    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // 即使服务器端登出失败，也要清除本地token
      console.warn('服务器端登出失败:', error);
    } finally {
      this.removeStoredToken();
    }
  }

  async sendOTP(request: SendOTPRequest): Promise<{ success: boolean; message?: string; expires_at?: string }> {
    return this.request('/auth/send-otp', {
      method: 'POST',
      data: request,
    });
  }

  async verifyOTP(request: VerifyOTPRequest): Promise<{ success: boolean; message?: string; email?: string }> {
    return this.request('/auth/verify-otp', {
      method: 'POST',
      data: request,
    });
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<{ success: boolean }> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      data: request,
    });
  }

  async resetPassword(request: ResetPasswordRequest): Promise<{ success: boolean }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      data: request,
    });
  }

  // 新增社交登录方法
  async socialLogin(user: Auth0User): Promise<{ token: string; user: User }> {
    const response = await this.request<{ token: string; user: User }>('/auth/social-login', {
      method: 'POST',
      data: user,
    });

    if (response.token) {
      this.setStoredToken(response.token);
    }

    return response;
  }

  // 新增便捷方法，支持新的参数格式
  async loginWithCredentials(
    email: string, 
    password: string, 
    turnstileToken?: string, 
    code?: string
  ): Promise<LoginResponse> {
    return this.login({
      email,
      password,
      code,
      turnstileToken,
    });
  }

  async registerWithCredentials(
    email: string,
    password: string,
    name: string,
    code: string,
    turnstileToken?: string,
    invitationCode?: string
  ): Promise<RegisterResponse> {
    return this.register({
      email,
      password,
      name,
      code,
      turnstileToken,
      invitationCode,
    });
  }

  async sendOTPWithParams(
    email: string,
    isLogin?: number,
    purpose: 'registration' | 'forgot_password' | 'mfa' = 'registration',
    turnstileToken?: string,
    invite_code?: string
  ): Promise<{ success: boolean; message?: string; expires_at?: string }> {
    return this.sendOTP({
      email,
      isLogin,
      purpose,
      turnstileToken,
      invite_code,
    });
  }

  async verifyOTPWithParams(
    email: string,
    code: string,
    purpose: 'registration' | 'forgot_password' | 'mfa' = 'registration'
  ): Promise<{ success: boolean; message?: string; email?: string }> {
    return this.verifyOTP({
      email,
      code,
      purpose,
    });
  }

  async getCurrentUser(): Promise<UserProfile | null> {
    try {
      const response = await this.request<{ user: UserProfile }>('/user/profile');
      return response.user;
    } catch (error) {
      if (error instanceof AuthError && error.code === 'unauthorized') {
        this.removeStoredToken();
      }
      return null;
    }
  }

  async updateUserProfile(profileData: Partial<User>): Promise<UpdateProfileResponse> {
    return await this.request<UpdateProfileResponse>('/user/profile', {
      method: 'PUT',
      data: profileData
    });
  }

  async updatePassword(request: UpdatePasswordRequest): Promise<UpdatePasswordResponse> {
    return await this.request<UpdatePasswordResponse>('/user/update-password', {
      method: 'POST',
      data: request
    });
  }

  async deleteAccount(): Promise<DeleteAccountResponse> {
    const response = await this.request<DeleteAccountResponse>('/user/close', {
      method: 'POST'
    });

    // 删除账户成功后清除本地token
    if (response.success) {
      this.removeStoredToken();
    }

    return response;
  }

  async refreshToken(): Promise<string | null> {
    try {
      const response = await this.request<{ token: string }>('/auth/refresh');
      if (response.token) {
        this.setStoredToken(response.token);
        return response.token;
      }
    } catch (error) {
      this.removeStoredToken();
    }
    return null;
  }

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }
}

// 导出单例实例
export const authService = new AuthService(import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'); 
